// Estimates how noisy the forward projection is, based on how much
// historical monthly net cashflow has actually swung around its
// recurring baseline. Used to render a P10/P90 cone on the chart.
//
// The signal we want is *unexplained* variance: month-to-month noise
// after subtracting known recurring contributions. We treat that
// monthly residual as an i.i.d. sample from a normal distribution
// (rough but defensible at this scale), compute its stddev, and
// propagate it forward with σ_t = σ × √t — random-walk style.
//
// P10/P90 are p50 ± 1.28σ_t (the 80% interval).

import { seriesKeyOf } from './series-key.js';
import { isMoneyIn } from '../txType.js';

const Z_80 = 1.2815515655446004;

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }
  return new Date(value);
}

function startOfMonth(date) {
  const d = parseDate(date);
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(date, n) {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}

const MONTHLY_OCCURRENCES = { daily: 30, weekly: 4, biweekly: 2, monthly: 1, semiannual: 1 / 6, yearly: 1 / 12 };

// Returns sum of signed monthly contributions from all recurring
// templates active in `monthStart`. Mirrors how buildProjection's
// inner loop estimates recurring net. Series are identified by desc+type
// and contribute their AVERAGE instance amount (variable-bill aware).
export function recurringMonthlyNet(transactions) {
  const seriesMap = {};
  transactions
    .filter((t) => t.isRecurring)
    .forEach((t) => {
      const key = seriesKeyOf(t);
      const amt = Number(t.amount) || 0;
      if (!seriesMap[key]) {
        seriesMap[key] = { sum: amt, count: 1, type: t.type, recurrenceType: t.recurrenceType };
      } else {
        seriesMap[key].sum += amt;
        seriesMap[key].count += 1;
      }
    });
  let net = 0;
  Object.values(seriesMap).forEach(({ sum, count, type, recurrenceType }) => {
    const occ = MONTHLY_OCCURRENCES[recurrenceType] || 0;
    if (!occ) return;
    const amount = sum / count;
    net += (isMoneyIn(type) ? amount : -amount) * occ;
  });
  return net;
}

// Net of all transactions in the given calendar month.
export function monthlyActualNet(transactions, monthStart) {
  const start = startOfMonth(monthStart);
  let net = 0;
  transactions.forEach((t) => {
    const d = parseDate(t.date);
    if (!d) return;
    if (d.getFullYear() !== start.getFullYear() || d.getMonth() !== start.getMonth()) return;
    const amt = Number(t.amount) || 0;
    net += isMoneyIn(t.type) ? amt : -amt;
  });
  return net;
}

// Variable (residual) net for one historical month = actual − recurring expected.
export function monthlyVariableNet(transactions, monthStart) {
  const recurring = recurringMonthlyNet(transactions);
  return monthlyActualNet(transactions, monthStart) - recurring;
}

// Compute μ, σ over the last N COMPLETED prior months (excludes current).
export function varianceFromHistory(transactions, options = {}) {
  const { lookbackMonths = 6, today = new Date() } = options;
  const currentMonth = startOfMonth(today);
  const samples = [];
  for (let i = 1; i <= lookbackMonths; i++) {
    samples.push(monthlyVariableNet(transactions, addMonths(currentMonth, -i)));
  }
  const sampleSize = samples.length;
  const mean = samples.reduce((s, v) => s + v, 0) / sampleSize;
  const variance =
    sampleSize > 1
      ? samples.reduce((s, v) => s + (v - mean) * (v - mean), 0) / (sampleSize - 1)
      : 0;
  const stddev = Math.sqrt(variance);
  return { mean, stddev, sampleSize, samples };
}

// Decorate a baseline projection with p10/p90. Each entry of the baseline
// is `{label, balance, ...}` — we add `p50` (= balance), `p10`, `p90`.
//
// σ at month t is σ × √t (cumulative variance under i.i.d. assumption).
// When confidence is low (sampleSize < minSamples), bands collapse to p50
// rather than scream at the user with a noisy wedge from 1-2 data points.
export function projectionBands(projection, varianceStats, options = {}) {
  const { minSamples = 3, z = Z_80 } = options;
  const { stddev, sampleSize } = varianceStats || {};
  const reliable = sampleSize >= minSamples && Number.isFinite(stddev) && stddev > 0;
  return projection.map((point, idx) => {
    const t = idx + 1;
    const halfWidth = reliable ? z * stddev * Math.sqrt(t) : 0;
    return {
      ...point,
      p50: point.balance,
      p10: point.balance - halfWidth,
      p90: point.balance + halfWidth,
    };
  });
}

export const _constants = { Z_80 };
